import * as ko from 'knockout';
import * as localisationHelper from 'localisationHelper';
import * as Signals from 'signals';

// Services
import { ValidationService } from "Content/ts/Shared/Services/Validation/validationService";

// Components
import 'Content/ts/shared/ConfirmationModal/confirmation-modal';

export interface BankDetailsParams {
    AccountNumber: KnockoutObservable<string>;
    SortCode: KnockoutObservable<string>;
    ValidationFields: KnockoutObservable<any>[];
    UpdateSignal: Signals.Signal;
    DirectDebitInitialise: Signals.Signal;
    AutoInitialise: boolean;
    IsRequired: KnockoutObservable<boolean>;
}

export class DDPNZ {
    customMessages: {
        AccountNumber: string;
        AccountNumberMin: string;
        AccountNumberMax: string;
        AccountNumberInvalid: string;

        BankNumber: string;
        BankNumberMin: string;
        BankNumberMax: string;
        BankNumberInvalid: string;

        AccountSuffix: string;
        AccountSuffixMin: string;
        AccountSuffixMax: string;
        AccountSuffixInvalid: string;

        BranchNumber: string;
        BranchNumberMin: string;
        BranchNumberMax: string;
        BranchNumberInvalid: string;

        CombinedSortCodeMin: string;
        CombinedSortCodeMax: string;
    };

    genericAccountNumber: KnockoutObservable<string>;
    genericSortCode: KnockoutObservable<string>;

    accountNumber: KnockoutObservable<string>;
    bankNumber: KnockoutObservable<string>;
    branchNumber: KnockoutObservable<string>;
    accountSuffix: KnockoutObservable<string>;
    validationFields: KnockoutObservable<any>[];
    isRequired: KnockoutObservable<boolean>;
    autoInitialise: boolean;
    firstLoad: boolean;

    updateSignal: Signals.Signal;
    directDebitInitialise: Signals.Signal;

    constructor(params: BankDetailsParams) {
        this.genericAccountNumber = params.AccountNumber;
        this.genericSortCode = params.SortCode;

        this.updateSignal = params.UpdateSignal;
        this.validationFields = params.ValidationFields;
        this.directDebitInitialise = params.DirectDebitInitialise;
        this.autoInitialise = params.AutoInitialise ?? true;
        this.isRequired = params.IsRequired;

        this.accountNumber = ko.observable(null);
        this.accountSuffix = ko.observable(null);

        this.bankNumber = ko.observable(null);
        this.branchNumber = ko.observable(null);

        this.firstLoad = true;

        if (this.autoInitialise) {
            this.initialise();
        }
        else {
            this.directDebitInitialise.add(this.initialiseSignalMethod);
        }

        this.retrieveCustomMessages();
    }

    private initialiseSignalMethod = (enabled: boolean): void => {
        if (enabled) {
            this.initialise();
        }
        else {
            if (!this.firstLoad) {
                this.dispose();
            }
        }
    };

    private retrieveCustomMessages(): void {
        this.customMessages = {
            AccountNumber: localisationHelper.getString("GenericResources", "AccountNumber"),
            AccountNumberMin: localisationHelper.getString("Validation", "PNZAccountNumberMin"),
            AccountNumberMax: localisationHelper.getString("GenericResources", "Validation_AccountNumber"),
            AccountNumberInvalid: localisationHelper.getString("Validation", "GenericAccountNumberInvalid"),

            BankNumber: localisationHelper.getString("GenericResources", "BankNumber"),
            BankNumberMin: localisationHelper.getString("Validation", "PNZBankNumberMin"),
            BankNumberMax: localisationHelper.getString("Validation", "PNZBankNumberMax"),
            BankNumberInvalid: localisationHelper.getString("Validation", "PNZBankNumberInvalid"),

            AccountSuffix: localisationHelper.getString("GenericResources", "AccountSuffix"),
            AccountSuffixMax: localisationHelper.getString("Validation", "PNZAccountSuffixMax"),
            AccountSuffixMin: localisationHelper.getString("Validation", "PNZAccountSuffixMin"),
            AccountSuffixInvalid: localisationHelper.getString("Validation", "PNZAccountSuffixInvalid"),

            BranchNumber: localisationHelper.getString("GenericResources", "DD_BranchNumber"),
            BranchNumberMin: localisationHelper.getString("Validation", "PNZBranchNumberMin"),
            BranchNumberMax: localisationHelper.getString("Validation", "PNZBranchNumberMax"),
            BranchNumberInvalid: localisationHelper.getString("Validation", "GenericBranchNumberInvalid"),

            CombinedSortCodeMin: localisationHelper.getString("Validation", "CombinedSortCodeMin"),
            CombinedSortCodeMax: localisationHelper.getString("Validation", "CombinedSortCodeMax")
        }
    }

    private setupValidationFields(): void {
        // Comprised of account number and suffix (in that order)
        ValidationService.AddValidationRule(this.genericAccountNumber, this.validationFields, {
            // these validation messages shouldn't display but useful to ensure combined length is correct
            minLength: {
                params: 7,
                message: this.customMessages.AccountNumberMin
            },
            maxLength: {
                params: 10,
                message: this.customMessages.AccountNumberMax
            }
        });

        ValidationService.AddValidationRule(this.accountNumber, this.validationFields, {
            minLength: {
                params: 7,
                message: this.customMessages.AccountNumberMin
            },
            maxLength: {
                params: 7,
                message: this.customMessages.AccountNumberMax
            },
            pattern: {
                message: this.customMessages.AccountNumberInvalid,
                params: /^[0-9]*$/
            }
        });

        ValidationService.AddValidationRule(this.accountSuffix, this.validationFields, {
            minLength: {
                params: 2,
                message: this.customMessages.AccountSuffixMin
            },
            maxLength: {
                params: 3,
                message: this.customMessages.AccountSuffixMax
            },
            pattern: {
                message: this.customMessages.AccountSuffixInvalid,
                params: /^[0-9]*$/
            }
        });

        // Comprised of the bank number and branch number (in that order)
        ValidationService.AddValidationRule(this.genericSortCode, this.validationFields, {
            // these validation messages shouldn't display but useful to ensure combined length is correct
            minLength: {
                params: 6,
                message: this.customMessages.CombinedSortCodeMin
            },
            maxLength: {
                params: 6,
                message: this.customMessages.CombinedSortCodeMax
            }
        });

        ValidationService.AddValidationRule(this.bankNumber, this.validationFields, {
            minLength: {
                params: 2,
                message: this.customMessages.BankNumberMin
            },
            maxLength: {
                params: 2,
                message: this.customMessages.BankNumberMax
            },
            pattern: {
                params: /^[0-9]*$/,
                message: this.customMessages.BankNumberInvalid
            }
        });

        ValidationService.AddValidationRule(this.branchNumber, this.validationFields, {
            minLength: {
                params: 4,
                message: this.customMessages.BranchNumberMin
            },
            maxLength: {
                params: 4,
                message: this.customMessages.BranchNumberMax
            },
            pattern: {
                params: /^[0-9]*$/,
                message: this.customMessages.BranchNumberInvalid
            }
        });
    }

    private removeValidationFields(): void {
        ValidationService.RemoveValidationRule(this.genericAccountNumber, this.validationFields);
        ValidationService.RemoveValidationRule(this.accountNumber, this.validationFields);
        ValidationService.RemoveValidationRule(this.accountSuffix, this.validationFields);
        ValidationService.RemoveValidationRule(this.genericSortCode, this.validationFields);
        ValidationService.RemoveValidationRule(this.bankNumber, this.validationFields);
        ValidationService.RemoveValidationRule(this.branchNumber, this.validationFields);
    }

    // These subscriptions need to be created after the validation so that
    // the validation can run before the subscription is fired. If not the knockout
    // validation will still be validating on the previous value
    private setupSubscriptions(): void {
        this.genericAccountNumber.subscribe(() => {
            this.updateSignal.dispatch();
        });

        this.genericSortCode.subscribe(() => {
            this.updateSignal.dispatch();
        });

        this.accountNumber.subscribe(() => {
            // check that the observables aren't null because ko will tostring null!
            if (this.accountNumber() !== null && this.accountSuffix() !== null) {
                this.genericAccountNumber(this.accountNumber() + this.accountSuffix());
            }
        });

        this.accountSuffix.subscribe(() => {
            // check that the observables aren't null because ko will tostring null!
            if (this.accountNumber() !== null && this.accountSuffix() !== null) {
                this.genericAccountNumber(this.accountNumber() + this.accountSuffix());
            }
        });

        this.bankNumber.subscribe(() => {
            // check that the observables aren't null because ko will tostring null!
            if (this.bankNumber() !== null && this.branchNumber() !== null) {
                this.genericSortCode(this.bankNumber() + this.branchNumber());
            }
        });

        this.branchNumber.subscribe(() => {
            // check that the observables aren't null because ko will tostring null!
            if (this.bankNumber() !== null && this.branchNumber() !== null) {
                this.genericSortCode(this.bankNumber() + this.branchNumber());
            }
        });
    }

    private initialise(): void {
        this.setupValidationFields();
        this.setupSubscriptions();
        this.firstLoad = false;
    }

    public dispose(): void {
        this.removeValidationFields();
        this.directDebitInitialise.remove(this.initialiseSignalMethod);
    }
}

const componentName = 'dd-pnz';
ko.components.unregister(componentName);
ko.components.register(componentName, {
    viewModel: DDPNZ,
    template: { require: `text!/Themes/Default/Content/ts/Shared/DirectDebitDetails/${componentName}.html` }
});